import { Box, Text, VStack } from '@chakra-ui/react';
import type WebUser from 'src/auth/WebUser';
import { useI18n } from 'src/i18n/I18nContext';
import {
    AccordionItem,
    AccordionItemContent,
    AccordionItemTrigger,
    AccordionRoot,
} from '@/components/ui/accordion';
import {
    DrawerBackdrop,
    DrawerBody,
    DrawerCloseTrigger,
    DrawerContent,
    DrawerHeader,
    DrawerRoot,
} from '@/components/ui/drawer';
import HeaderBrand from './HeaderBrand';
import { UserIdentityCard } from './HeaderProfile';
import {
    HeaderMenuIcon,
    HeaderNavigationBadge,
    MobileNavLink,
} from './NavigationPrimitives';
import {
    getAllowedMenuNodes,
    getMenuNodeBadgeCount,
    isHeaderLinkNode,
    isMenuNodeActive,
} from './navigation';
import type { HeaderBadgeCounts, HeaderMenuNode } from './navigation';

interface MobileNavigationDrawerProps {
    open: boolean;
    user: WebUser | null;
    menuNodes: HeaderMenuNode[];
    badgeCounts?: HeaderBadgeCounts;
    path: string;
    onOpenChange: (open: boolean) => void;
    onNavigate: (link: string) => void;
}

const MobileNavigationDrawer = ({
    open,
    user,
    menuNodes,
    badgeCounts = {},
    path,
    onOpenChange,
    onNavigate,
}: MobileNavigationDrawerProps) => {
    const { t } = useI18n();
    const allowedNodes = getAllowedMenuNodes(menuNodes, user?.data.role);
    const activeSection = allowedNodes.find((node) =>
        node.nodes && isMenuNodeActive(node, path)
    )?.nameKey;

    return (
        <DrawerRoot
            placement='start'
            size='sm'
            open={open}
            onOpenChange={({ open: nextOpen }) => onOpenChange(nextOpen)}
        >
            <DrawerBackdrop bg='blackAlpha.500' backdropFilter='blur(4px)' />
            <DrawerContent bg='bg.panel' maxW='22rem'>
                <DrawerCloseTrigger top='4' insetEnd='4' rounded='full' />
                <DrawerHeader
                    minH='5rem'
                    px='5'
                    py='4'
                    borderBottomWidth='1px'
                    borderColor='border.subtle'
                >
                    <HeaderBrand user={user?.data ?? null} variant='drawer' />
                </DrawerHeader>

                <DrawerBody px='3' py='4'>
                    {user && <UserIdentityCard user={user} marginBottom='4' />}

                    <Text
                        px='3'
                        mb='2'
                        fontSize='xs'
                        fontWeight='bold'
                        color='fg.subtle'
                        textTransform='uppercase'
                        letterSpacing='wider'
                    >
                        {t('header.navigation')}
                    </Text>

                    <AccordionRoot
                        variant='plain'
                        collapsible
                        defaultValue={activeSection ? [activeSection] : []}
                    >
                        {allowedNodes.map((node) => {
                            const active = isMenuNodeActive(node, path);
                            const badgeCount = getMenuNodeBadgeCount(node, badgeCounts);

                            if (isHeaderLinkNode(node)) {
                                return (
                                    <Box key={`mobile-${node.nameKey}`} mb='1'>
                                        <MobileNavLink
                                            node={node}
                                            role={user?.data.role}
                                            path={path}
                                            badgeCount={badgeCount}
                                            onNavigate={onNavigate}
                                        />
                                    </Box>
                                );
                            }

                            return (
                                <AccordionItem
                                    key={`mobile-${node.nameKey}`}
                                    value={node.nameKey}
                                    border='0'
                                    mb='1'
                                >
                                    <AccordionItemTrigger
                                        px='3'
                                        py='2.5'
                                        rounded='xl'
                                        color={active ? 'blue.fg' : 'fg'}
                                        bg={active ? 'blue.subtle' : 'transparent'}
                                        _hover={{ bg: active ? 'blue.muted' : 'bg.subtle' }}
                                    >
                                        <HeaderMenuIcon node={node} active={active} />
                                        <Text flex='1' fontWeight={active ? 'semibold' : 'medium'}>
                                            {t(node.nameKey)}
                                        </Text>
                                        <HeaderNavigationBadge count={badgeCount} />
                                    </AccordionItemTrigger>
                                    <AccordionItemContent px='2' pt='1' pb='2'>
                                        <VStack
                                            align='stretch'
                                            gap='1'
                                            ps='3'
                                            borderLeftWidth='1px'
                                            borderColor='border.subtle'
                                        >
                                            {getAllowedMenuNodes(node.nodes ?? [], user?.data.role).map((child, childIndex) =>
                                                isHeaderLinkNode(child) ? (
                                                    <MobileNavLink
                                                        key={`mobile-sub-${child.nameKey}`}
                                                        node={child}
                                                        role={user?.data.role}
                                                        path={path}
                                                        badgeCount={getMenuNodeBadgeCount(child, badgeCounts)}
                                                        nested
                                                        onNavigate={onNavigate}
                                                    />
                                                ) : (
                                                    <Box
                                                        key={`mobile-separator-${childIndex}`}
                                                        h='1px'
                                                        my='1'
                                                        bg='border.subtle'
                                                    />
                                                )
                                            )}
                                        </VStack>
                                    </AccordionItemContent>
                                </AccordionItem>
                            );
                        })}
                    </AccordionRoot>
                    {!user && (
                        <VStack align='stretch' gap='2' mt='5' px='1'>
                            <MobileNavLink
                                node={{ nameKey: 'header.login', link: '/login/' }}
                                path={path}
                                onNavigate={onNavigate}
                            />
                        </VStack>
                    )}
                </DrawerBody>
            </DrawerContent>
        </DrawerRoot>
    );
};

export default MobileNavigationDrawer;
